import React, { useEffect, useState } from "react";
import { graphql } from "gatsby";
import Layout from "@qlik/qlik/src/components/Layout";
import Seo from "@qlik/qlik/src/components/Seo";
import { renderLinkedComponents } from "@qlik/qlik/src/components/registry";
import NewsTemplate from "@qlik/qlik/src/components/InnerPages/News";
import { formatDatePost } from "@qlik/qlik/src/lib/util/dates";
import SocialShare from "@qlik/qlik/src/components/Common/SocialShare";
import { renderRichText } from "gatsby-source-contentful/rich-text";
import * as bannerStyles from "./styles.module.css";
import { NEWS_PATHNAME } from "@qlik/qlik/gatsbyNode/constants";

/**
 * GraphQL query to retrieve all data for the flexible page.
 * Gatsby only allows one dynamic query per page.
 */
export const query = graphql`
    query($id: String, $headerName: String, $locale: String) {
        contentfulHeader(name: { eq: $headerName }, node_locale: { eq: $locale }) {
            ...Header
        }
        contentfulNews(id: { eq: $id }) {
            ...News
        }
        contentfulFooter(name: { eq: "Full footer" }, node_locale: { eq: $locale }) {
            ...Footer
        }
        contentfulThemeSettings {
            ...ThemeSettings
        }
    }
`;

/**
 * News component
 * This component retrieves components from the registry based on content type and rendering name
 * @param {Object} data - The data object returned from the GraphQL query
 * @returns {JSX.Element} - The rendered News component
 */
const News = ({ data, pageContext }) => {
    const [formattedDate, setFormattedDate] = useState("");
    // This renders the inner page data inside the page template component
    const bodyContent = <NewsTemplate data={data.contentfulNews} />;
    const pageComponents = data.contentfulNews.components?.map((el, i) => renderLinkedComponents(el, i));

    useEffect(() => {
        const dateTime = data?.contentfulNews?.dateTime;

        if (dateTime) {
            const formatted = formatDatePost(dateTime, pageContext?.locale);
            setFormattedDate(formatted);
        }
    }, [data?.contentfulNews?.dateTime]);

    return (
        <Layout
            headerData={data.contentfulHeader}
            minimalHeaderData={data.contentfulThemeSettings?.minimalNavPages}
            footerData={data.contentfulFooter}
            pageSlug={data.contentfulNews.slug}
            dynamicHeaderCta={data.contentfulThemeSettings?.dynamicHeaderCta}
            searchPopupData={data.contentfulThemeSettings?.searchPopupMicrocopyGroup}
        >
            <Seo
                data={data.contentfulNews.metadata}
                pageSlug={`${NEWS_PATHNAME}${data.contentfulNews.slug?.startsWith("/") ? "" : "/"}${
                    data.contentfulNews.slug
                }`}
            />
            <div className="news-room-inner-content relative">
                <section className={`news-room-inner-banner ${bannerStyles.newsRoomInnerBanner}`}>
                    <div className="container">
                        <div className="py-10 xl:pt-20 xl:pb-14 lg:mx-25">
                            {data?.contentfulNews?.title && (
                                <h1 className="title-2 text-with-color mb-5 xl:mb-15">{data?.contentfulNews?.title}</h1>
                            )}

                            <SocialShare
                                pageTitle={data?.contentfulNews?.title}
                                pageSlug={`news/${data?.contentfulNews?.slug}`}
                                startElementSelector=".news-room-inner-content"
                                stopElementSelector=".news-room-inner-content"
                                startElementPosition="top"
                                stopElementPosition="bottom"
                                additionalClasses="lg:mb-25"
                            />

                            {data?.contentfulNews?.newsSubtitle && (
                                <div className="news-room-inner-subtitle mb-5 xl:mb-[55px]">
                                    {renderRichText(data?.contentfulNews?.newsSubtitle)}
                                </div>
                            )}

                            {data?.contentfulNews?.dateTime && (
                                <div className="dateTime mb-0 text-stone text-base tracking-wide5 uppercase font-medium">
                                    {formattedDate}
                                </div>
                            )}
                        </div>
                    </div>
                </section>
                {bodyContent}
            </div>

            {pageComponents}
        </Layout>
    );
};
export default News;
